import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  CircularProgress,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../contexts/AuthContext';
import { useError } from '../contexts/ErrorContext';
import { adminEndpoints } from '../services/api';

interface Package {
  id: string;
  description: string;
  cardCount: number;
  createdAt: string;
  updatedAt: string;
}

const Packages: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { showError } = useError();
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState<Package[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [newPackageId, setNewPackageId] = useState('');
  const [newPackageDescription, setNewPackageDescription] = useState('');
  const [installDialogOpen, setInstallDialogOpen] = useState(false);
  const [installStatus, setInstallStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [installMessage, setInstallMessage] = useState('');
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [downloadMessage, setDownloadMessage] = useState('');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await adminEndpoints.getPackages();
      if (response.data && response.data.packages) {
        setPackages(response.data.packages);
      } else {
        setPackages([]);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      showError('Failed to fetch packages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePackage = async () => {
    try {
      setLoading(true);
      await adminEndpoints.createPackage(newPackageId, newPackageDescription);
      await fetchPackages();
      setOpenDialog(false);
      setNewPackageId('');
      setNewPackageDescription('');
    } catch (error) {
      console.error('Error creating package:', error);
      showError('Failed to create package');
    } finally {
      setLoading(false);
    }
  };

  const handleInstallAllWords = async () => {
    try {
      setInstallDialogOpen(true);
      setInstallStatus('loading');
      setInstallMessage('Downloading all words...');
      
      const response = await adminEndpoints.getAllWords();
      
      // Create a blob from the response data
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = 'all_words.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setInstallStatus('success');
      setInstallMessage('All words downloaded successfully!');
    } catch (error) {
      console.error('Error downloading words:', error);
      setInstallStatus('error');
      setInstallMessage('Failed to download words. Please try again.');
    }
  };

  const handleDownloadAllWordsCombined = async () => {
    try {
      setDownloadDialogOpen(true);
      setDownloadStatus('loading');
      setDownloadMessage('Downloading all words (combined)...');

      const response = await adminEndpoints.getAllWordsCombined();

      // Create a blob from the response data
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = 'all_words_combined.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setDownloadStatus('success');
      setDownloadMessage('All words (combined) downloaded successfully!');
    } catch (error) {
      console.error('Error downloading combined words:', error);
      setDownloadStatus('error');
      setDownloadMessage('Failed to download words. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: 2,
        mb: 4 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Manage Packages
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row',
          gap: 1,
          width: { xs: '100%', sm: 'auto' },
          flexWrap: 'wrap'
        }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DownloadIcon />}
            onClick={handleInstallAllWords}
            disabled={loading}
            sx={{ 
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '0.875rem' },
              whiteSpace: 'nowrap'
            }}
          >
            Install All Words
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadAllWordsCombined}
            disabled={loading}
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '0.875rem' },
              whiteSpace: 'nowrap'
            }}
          >
            Download All Words (Combined)
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            disabled={loading}
            sx={{ 
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '0.875rem' },
              whiteSpace: 'nowrap'
            }}
          >
            Create
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {packages.map((pkg) => (
            <Grid item xs={12} sm={6} md={4} key={pkg.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="div" gutterBottom>
                    {pkg.id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {pkg.description || 'No description provided'}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Cards:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {pkg.cardCount}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Created:
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(pkg.createdAt)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Updated:
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(pkg.updatedAt)}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    color="primary"
                    onClick={() => navigate(`/admin/packages/${pkg.id}`)}
                  >
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Create New Package</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Package ID"
            type="text"
            fullWidth
            value={newPackageId}
            onChange={(e) => setNewPackageId(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={newPackageDescription}
            onChange={(e) => setNewPackageDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleCreatePackage} color="primary" disabled={!newPackageId || loading}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={installDialogOpen} onClose={() => setInstallDialogOpen(false)}>
        <DialogTitle>Install All Words</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
            {installStatus === 'loading' && <CircularProgress sx={{ mb: 2 }} />}
            <Typography variant="body1" color={installStatus === 'error' ? 'error' : 'text.primary'}>
              {installMessage}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setInstallDialogOpen(false)}
            disabled={installStatus === 'loading'}
          >
            {installStatus === 'success' ? 'Done' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={downloadDialogOpen} onClose={() => setDownloadDialogOpen(false)}>
        <DialogTitle>Download All Words (Combined)</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
            {downloadStatus === 'loading' && <CircularProgress sx={{ mb: 2 }} />}
            <Typography variant="body1" color={downloadStatus === 'error' ? 'error' : 'text.primary'}>
              {downloadMessage}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDownloadDialogOpen(false)}
            disabled={downloadStatus === 'loading'}
          >
            {downloadStatus === 'success' ? 'Done' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Packages; 